import { useState, type FormEvent } from 'react';
import { api, type Job } from '../lib/api';
import { useJobs } from '../components/JobToast';

interface PlatformCheck {
  name: string;
  url: string;
  icon: string;
  found: boolean | null;
}

export function LookupPage() {
  const { trackJob } = useJobs();
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [social, setSocial] = useState<PlatformCheck[]>([]);
  const [checkingSocial, setCheckingSocial] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    setSocial([]);
    try {
      const payload: { username?: string; userId?: number } = {};
      if (username.trim()) payload.username = username.trim();
      if (userId.trim()) payload.userId = Number(userId);
      const { job } = await api.post<{ job: Job }>('/api/jobs/lookup', payload);
      const done = await trackJob(job.id, 'Gebruiker ophalen');
      if (done.status === 'failed') {
        setError(done.error || 'Ophalen mislukt');
      } else {
        const result = done.result as { info?: Record<string, unknown> } | null;
        setInfo(result?.info ?? null);
        const un =
          (result?.info?.username as string | null | undefined) ||
          username.replace(/^@/, '').trim() ||
          '';
        if (un) void checkSocial(un);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ophalen mislukt');
    } finally {
      setBusy(false);
    }
  };

  const checkSocial = async (un: string) => {
    setCheckingSocial(true);
    try {
      const { platforms } = await api.get<{
        platforms: Array<{ name: string; url: string; icon: string }>;
      }>('/api/jobs/meta/social-platforms');
      const initial: PlatformCheck[] = platforms.map((p) => ({
        ...p,
        url: p.url.replace('{u}', encodeURIComponent(un)),
        found: null,
      }));
      setSocial(initial);
      await Promise.all(
        initial.map(async (p, idx) => {
          try {
            const r = await api.post<{ found: boolean }>('/api/jobs/profile-check', { url: p.url });
            setSocial((prev) =>
              prev.map((row, i) => (i === idx ? { ...row, found: r.found } : row)),
            );
          } catch {
            setSocial((prev) => prev.map((row, i) => (i === idx ? { ...row, found: null } : row)));
          }
        }),
      );
    } finally {
      setCheckingSocial(false);
    }
  };

  return (
    <div>
      <h1>Gebruiker ophalen</h1>
      <p className="page-lead">
        Haal Telegram-profielinfo op via een achtergrondjob en check daarna dezelfde username op andere
        platforms.
      </p>

      <form className="panel toolbar" onSubmit={(e) => void onSubmit(e)}>
        <div className="field" style={{ flex: 1 }}>
          <label>Username</label>
          <input
            className="input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="@naam"
          />
        </div>
        <div className="field">
          <label>of ID</label>
          <input
            className="input input-md"
            type="number"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="123456"
          />
        </div>
        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Bezig…' : 'Ophalen'}
        </button>
      </form>

      {error && <div className="alert error">{error}</div>}

      {info && (
        <div className="panel" style={{ marginTop: 14 }}>
          <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Telegram</h2>
          <div className="msg-list">
            {Object.entries(info).map(([k, v]) => (
              <div key={k} className="row">
                <strong style={{ minWidth: 110 }}>{k}</strong>
                <span>{String(v ?? '—')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {social.length > 0 && (
        <div className="panel" style={{ marginTop: 14 }}>
          <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Andere platforms</h2>
          <p className="muted">
            {checkingSocial ? 'Profielen controleren…' : 'Klik een platform om te openen.'}
          </p>
          <div className="social-grid">
            {social.map((p) => (
              <a
                key={p.name}
                className={`social-card ${
                  p.found === true ? 'found' : p.found === false ? 'missing' : 'loading'
                }`}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <strong>{p.name}</strong>
                <span className="muted">
                  {p.found === true ? 'Gevonden' : p.found === false ? 'Niet gevonden' : 'Controleren…'}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
